/**
 * What «Зафиксировать» records, top down (decisions-log V163): first what the change is about —
 * «Клиент», «Проект», «Команда», «Юрлицо» — then that subject's actions, each a capture type with
 * the values it starts from (a rate of a project or of a person), or «Новое юрлицо» (V143), which
 * creates a blank entity and opens its card; «Новый клиент» is a form like «Новый проект» (V181). Digit keys pick a tile on either level.
 */
import type { CaptureType } from '@outsource/shared';
import {
  BadgePercent,
  BriefcaseBusiness,
  Banknote,
  Building2,
  CirclePause,
  Folder,
  FolderPlus,
  Gift,
  IdCard,
  Landmark,
  Palmtree,
  StickyNote,
  UserPen,
  UserPlus,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Icon } from '../../components/ui/Icon';

export type CaptureGroup = 'client' | 'project' | 'team' | 'entity';

export interface CaptureAction {
  readonly label: string;
  readonly icon: LucideIcon;
  /** The capture type, or `newEntity` that creates a blank entity and opens its card. */
  readonly type: CaptureType | 'newEntity';
  /** Values the form starts from. */
  readonly values?: Readonly<Record<string, string>>;
}

export interface CaptureGroupSpec {
  readonly group: CaptureGroup;
  readonly label: string;
  readonly icon: LucideIcon;
  readonly actions: readonly CaptureAction[];
}

const NOTE: CaptureAction = { type: 'note', label: 'Уточнить позже', icon: StickyNote };
const NEW_PROJECT: CaptureAction = { type: 'project', label: 'Новый проект', icon: FolderPlus };

/**
 * Two opposite events behind one tile (V233): the form starts with the first and a switch at its top
 * changes to the other, keeping what is typed — «Выходит / уходит» on a project (V202), «Оформление /
 * увольнение» in an entity (V178, V203).
 */
export interface CapturePair {
  readonly label: string;
  readonly options: readonly (readonly [CaptureType, string])[];
}

const ASSIGNMENT_PAIR: CapturePair = {
  label: 'Выходит / уходит с проекта',
  options: [
    ['join', 'Выходит на проект'],
    ['leave', 'Уходит с проекта'],
  ],
};
const EMPLOYMENT_PAIR: CapturePair = {
  label: 'Оформление / увольнение из юрлица',
  options: [
    ['employ', 'Оформление'],
    ['dismiss', 'Увольнение'],
  ],
};

/** The pair a capture type belongs to, if any. */
export const pairOf = (type: CaptureType | null): CapturePair | null =>
  [ASSIGNMENT_PAIR, EMPLOYMENT_PAIR].find((pair) => pair.options.some(([value]) => value === type)) ?? null;

const ASSIGNMENT: CaptureAction = { type: 'join', label: ASSIGNMENT_PAIR.label, icon: UserPlus };
const EMPLOYMENT: CaptureAction = { type: 'employ', label: EMPLOYMENT_PAIR.label, icon: BriefcaseBusiness };
/** V233: a vacation or unpaid leave on the salary through our entity. */
const ABSENCE: CaptureAction = { type: 'absence', label: 'Отпуск / за свой счёт', icon: Palmtree };
/** V287: a bonus or the aid to a молодой специалист through the entity paying the salary. */
const EXTRA: CaptureAction = { type: 'extra', label: 'Премия / денежная помощь', icon: Gift };

export const CAPTURE_GROUPS: readonly CaptureGroupSpec[] = [
  {
    group: 'client',
    label: 'Клиент',
    icon: Building2,
    actions: [
      { type: 'client', label: 'Новый клиент', icon: Building2 },
      { type: 'payment', label: 'Клиент оплатил', icon: Banknote },
      NEW_PROJECT,
      NOTE,
    ],
  },
  {
    group: 'project',
    label: 'Проект',
    icon: Folder,
    actions: [
      NEW_PROJECT,
      ASSIGNMENT,
      { type: 'rate', label: 'Изменение ставки проекта', icon: BadgePercent, values: { tkind: 'project' } },
      { type: 'status', label: 'Пауза / завершён', icon: CirclePause },
      NOTE,
    ],
  },
  {
    group: 'team',
    label: 'Команда',
    icon: Users,
    actions: [
      { type: 'person', label: 'Новый человек / условия', icon: IdCard },
      ASSIGNMENT,
      { type: 'rate', label: 'Изменение ставки человека', icon: BadgePercent, values: { tkind: 'assign' } },
      EMPLOYMENT,
      ABSENCE,
      EXTRA,
      { type: 'edit', label: 'Данные человека', icon: UserPen },
      NOTE,
    ],
  },
  {
    group: 'entity',
    label: 'Юрлицо',
    icon: Landmark,
    actions: [{ type: 'newEntity', label: 'Новое юрлицо', icon: Landmark }, EMPLOYMENT, EXTRA, NOTE],
  },
];

/** The title of a form opened by a preset: its pair's (V233), else the action of that type whose start values match it. */
export function actionLabelOf(type: CaptureType, values: Readonly<Record<string, string>> = {}): string {
  const pair = pairOf(type);
  if (pair) return pair.label;
  const actions = CAPTURE_GROUPS.flatMap((g) => g.actions).filter((a) => a.type === type);
  const matching = actions.find((a) => Object.entries(a.values ?? {}).every(([key, value]) => values[key] === value));
  return (matching ?? actions[0])?.label ?? 'Зафиксировать';
}

const TILE_ICON_PX = 18;

const TILE =
  'flex min-h-[92px] cursor-pointer flex-col items-start gap-2.5 rounded-2 border border-border-1 bg-bg-3 p-3.5 text-left text-fg-1 hover:border-border-2 hover:bg-bg-1';

function Tile({ icon, label, shortcut, onClick }: { readonly icon: LucideIcon; readonly label: string; readonly shortcut: string; readonly onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} aria-keyshortcuts={shortcut} className={TILE}>
      <span className="flex w-full items-center justify-between">
        <Icon icon={icon} size={TILE_ICON_PX} className="text-fg-2" />
        <span aria-hidden="true" className="text-[11px] font-semibold text-fg-3">
          {shortcut}
        </span>
      </span>
      <span className="text-[13px] font-semibold leading-[1.3]">{label}</span>
    </button>
  );
}

/** The tiles of one level: digit keys from 1. */
export function Tiles({ items, onPick }: { readonly items: ReadonlyArray<{ readonly label: string; readonly icon: LucideIcon }>; readonly onPick: (index: number) => void }) {
  return (
    <div className="grid grid-cols-4 gap-2 px-6 pb-6 pt-1">
      {items.map((item, index) => (
        <Tile key={item.label} icon={item.icon} label={item.label} shortcut={String(index + 1)} onClick={() => onPick(index)} />
      ))}
    </div>
  );
}
